import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { useParams } from "react-router-dom";

import SideBarForRole7 from "../components/sidebar/SideBarForRole7";
import NavbarForRole7 from "../components/navbar/NavbarForRole7";
import BodyComposition from "../components/bodyComposition";
import EmptyState from "../components/ui/EmptyState";
import { METRICS, getMetric } from "../config/metrics";
import "./reports.scss";

const storageKey = (watchId) => `reports:selectedMetrics:v1:${watchId ?? "all"}`;

const Reports = () => {
  const { watchId } = useParams();
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState("");
  const [restored, setRestored] = useState(false);

  /* --- Persistence ------------------------------------------------------ */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey(watchId));
      const parsed = raw ? JSON.parse(raw) : null;

      // v1 stored a { [label]: bool } map; normalise it to an ordered array
      // and drop metrics that no longer exist.
      const ids = Array.isArray(parsed)
        ? parsed
        : parsed && typeof parsed === "object"
        ? Object.keys(parsed).filter((key) => parsed[key])
        : [];

      setSelected(ids.filter((id) => getMetric(id)));
    } catch (error) {
      console.warn("Failed to restore selected metrics:", error);
    } finally {
      setRestored(true);
    }
  }, [watchId]);

  useEffect(() => {
    if (!restored) return;
    try {
      localStorage.setItem(storageKey(watchId), JSON.stringify(selected));
    } catch (error) {
      console.warn("Failed to persist selected metrics:", error);
    }
  }, [selected, watchId, restored]);

  /* --- Derived ---------------------------------------------------------- */
  const visibleMetrics = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return METRICS;
    return METRICS.filter((metric) =>
      `${metric.label} ${metric.short} ${metric.hint}`.toLowerCase().includes(needle)
    );
  }, [query]);

  const toggle = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );

  // Render in catalogue order so the report reads the same way every time,
  // regardless of the order metrics were ticked.
  const selectedMetrics = useMemo(
    () => METRICS.filter((metric) => selected.includes(metric.id)),
    [selected]
  );

  return (
    <div className="wg-shell">
      <SideBarForRole7 />

      <div className="wg-main">
        <NavbarForRole7 />

        <main className="wg-content">
          <div className="wg-page-head">
            <div className="wg-page-head__text">
              <p className="wg-eyebrow">Recorded history</p>
              <h1 className="wg-title">Reports</h1>
              <p className="wg-subtitle">
                Choose which vitals to review. Each selection opens its full
                record view with the same chart controls used on the dashboard.
              </p>
            </div>
            <div className="wg-page-head__actions">
              <Button
                variant="outlined"
                onClick={() => setSelected(METRICS.map((metric) => metric.id))}
                disabled={selected.length === METRICS.length}
              >
                Select all
              </Button>
              <Button onClick={() => setSelected([])} disabled={!selected.length}>
                Clear
              </Button>
            </div>
          </div>

          <BodyComposition />

          <div className="reports-body">
            <aside className="reports-sidebar">
              <section className="wg-card">
                <header className="wg-card__head">
                  <span className="wg-icon-badge">
                    <TuneOutlinedIcon />
                  </span>
                  <div className="wg-card__headings">
                    <h2 className="wg-card__title">Metrics</h2>
                    <div className="wg-card__sub">
                      {selected.length} of {METRICS.length} selected
                    </div>
                  </div>
                </header>

                <div className="wg-card__body wg-card__body--tight">
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search metrics"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <ul className="reports-metric-list">
                    {visibleMetrics.map((metric) => {
                      const active = selected.includes(metric.id);
                      return (
                        <li key={metric.id}>
                          <label
                            className={`reports-metric-option ${active ? "is-active" : ""}`}
                          >
                            <Checkbox
                              size="small"
                              checked={active}
                              onChange={() => toggle(metric.id)}
                            />
                            <span
                              className="wg-icon-badge wg-icon-badge--sm"
                              style={{ "--badge-color": `var(${metric.colorToken})` }}
                            >
                              {metric.icon}
                            </span>
                            <span className="reports-metric-option__name">
                              {metric.label}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                    {!visibleMetrics.length && (
                      <li className="wg-stat__meta" style={{ padding: "12px 8px" }}>
                        No metric matches “{query}”.
                      </li>
                    )}
                  </ul>
                </div>
              </section>
            </aside>

            <section className="reports-panels">
              {selectedMetrics.length === 0 ? (
                <EmptyState
                  icon={<TuneOutlinedIcon />}
                  title="No metrics selected"
                  copy="Tick a metric on the left to add its report. Your selection is saved per watch on this device."
                />
              ) : (
                <>
                  <p className="reports-summary">
                    Showing{" "}
                    <strong>
                      {selectedMetrics.length} report
                      {selectedMetrics.length === 1 ? "" : "s"}
                    </strong>{" "}
                    for watch <span className="wg-mono">{watchId}</span>
                  </p>
                  {selectedMetrics.map((metric) => (
                    <div key={metric.id}>{metric.render({})}</div>
                  ))}
                </>
              )}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Reports;
